import { useNavigate } from '@tanstack/react-router';
import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    readStoredAdminAgentTier,
    writeStoredAdminAgentTier,
} from '@apps/web/components/admin/admin_agent_chat_session_storage';
import {
    type AdminAgentChatSessionOptions,
    type AdminAgentRunCreateRequestWithPageContext,
    type AdminAgentRunRead,
    type Mode,
    type PhoebeAgentRunPendingEventRead,
} from '@apps/web/components/admin/admin_agent_chat_session_types';
import { type AdminAgentMessageDeliveryMode } from '@apps/web/components/admin/admin_agent_delivery_mode';
import { type ChatInputImageAttachment } from '@apps/web/components/phoebe_chat_view';
import { getApiClient } from '@apps/web/lib/api_client';
import { isTransparentRateLimitError } from '@apps/web/lib/known_api_errors';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { type AdminAgentTier } from '@apps/web/routes/_app/admin/agent/admin_agent_tier_picker';

const MAX_IMAGES_PER_TURN = 10;

// Per-send options beyond the raw content. `skillPreloadHints` are
// the suggested task's declared workflow packs (PHO-14248): sent
// with the first message so the backend can seed the runtime skill
// preload closure with them.
export type AdminAgentSubmitMessageOptions = {
    readonly skillPreloadHints?: ReadonlyArray<string>;
};

/**
 * `true` means submitted, `false` means a locally presentable failure, and
 * `void` keeps centrally owned failures and skipped sends silent to callers.
 */
export type AdminAgentSubmitMessageOutcome = boolean | void;

type AdminAgentUploadedImageRef = {
    readonly artifact_id: string;
    readonly run_id?: string | null;
    readonly mime_type: string;
    readonly size_bytes: number;
    readonly sha256: string;
    readonly filename?: string | null;
    readonly width?: number | null;
    readonly height?: number | null;
};

type AdminAgentImageAttachment = ChatInputImageAttachment & {
    readonly imageRef: AdminAgentUploadedImageRef | null;
};

type RunSettingPatchResult<TValue> = {
    readonly draftValue: TValue;
    readonly runPatch: Partial<AdminAgentRunRead>;
};

type UpdateRunSettingOptions<TValue> = {
    readonly fallbackErrorMessage: string;
    readonly isUpdating: boolean;
    readonly patchRun: (
        runId: string,
    ) => Promise<RunSettingPatchResult<TValue>>;
    readonly setDraftValue: (value: TValue) => void;
    readonly setIsUpdating: Dispatch<SetStateAction<boolean>>;
    readonly value: TValue;
};

export function useAdminAgentRunActions({
    initialPageContext,
    isSending,
    options,
    removeOptimisticPendingEvent,
    refreshRunEventStream,
    setIsSending,
    stageOptimisticUserMessage,
}: {
    readonly initialPageContext: AdminAgentChatSessionOptions['initialPageContext'];
    readonly isSending: boolean;
    readonly options: AdminAgentChatSessionOptions;
    readonly removeOptimisticPendingEvent: (eventId: string) => void;
    readonly refreshRunEventStream: () => void;
    readonly setIsSending: Dispatch<SetStateAction<boolean>>;
    readonly stageOptimisticUserMessage: (
        pendingEvent: PhoebeAgentRunPendingEventRead,
        runId: string,
    ) => void;
}) {
    const apiClient = getApiClient();
    const navigate = useNavigate();
    const [isCreatingRun, setIsCreatingRun] = useState(false);
    const [isUpdatingModel, setIsUpdatingModel] = useState(false);
    const [isUpdatingTier, setIsUpdatingTier] = useState(false);
    const [imageAttachments, setImageAttachments] = useState<
        Array<AdminAgentImageAttachment>
    >([]);
    const [draftTier, setDraftTier] = useState<AdminAgentTier>(
        () => options.defaultTier ?? readStoredAdminAgentTier(),
    );
    const [draftModel, setDraftModel] = useState<string | null>(
        options.defaultModel ?? null,
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const isSubmittingRef = useRef(false);
    const pendingDraftRunIdRef = useRef<string | null>(null);
    const pendingCreateRunPromiseRef = useRef<Promise<string | null> | null>(
        null,
    );
    const imageAttachmentsRef = useRef<Array<AdminAgentImageAttachment>>([]);

    useEffect(() => {
        imageAttachmentsRef.current = imageAttachments;
    }, [imageAttachments]);

    const updateDraftTier = useCallback(
        (tier: AdminAgentTier) => {
            setDraftTier(tier);
            if (options.persistTierPreference) {
                writeStoredAdminAgentTier(tier);
            }
        },
        [options.persistTierPreference],
    );

    useEffect(() => {
        return () => {
            for (const attachment of imageAttachmentsRef.current) {
                if (attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl);
                }
            }
        };
    }, []);

    const clearErrorMessage = useCallback(() => {
        setErrorMessage(null);
    }, []);

    const resetDraftRun = useCallback(() => {
        pendingDraftRunIdRef.current = null;
    }, []);

    const clearImageAttachments = useCallback(() => {
        for (const attachment of imageAttachmentsRef.current) {
            if (attachment.previewUrl) {
                URL.revokeObjectURL(attachment.previewUrl);
            }
        }
        imageAttachmentsRef.current = [];
        setImageAttachments([]);
    }, []);

    useEffect(() => {
        if (!options.selectedRunId) {
            return;
        }
        if (pendingDraftRunIdRef.current === options.selectedRunId) {
            pendingDraftRunIdRef.current = null;
            return;
        }
        pendingDraftRunIdRef.current = null;
        clearImageAttachments();
    }, [clearImageAttachments, options.selectedRunId]);

    const createRun = useCallback(async (): Promise<string | null> => {
        if (!options.listOrganizationId) {
            return null;
        }
        if (pendingCreateRunPromiseRef.current) {
            return pendingCreateRunPromiseRef.current;
        }

        const createRunPromise = (async (): Promise<string | null> => {
            setIsCreatingRun(true);
            setErrorMessage(null);
            try {
                const initialChatId =
                    options.initialChatId ??
                    (await options.ensureInitialChatId?.());
                const body: AdminAgentRunCreateRequestWithPageContext = {
                    organization_id: options.listOrganizationId,
                    mode: options.currentMode,
                    title: null,
                    tier: draftTier,
                    ...(draftModel ? { active_model: draftModel } : {}),
                    ...(initialPageContext
                        ? { page_context: initialPageContext }
                        : {}),
                    ...(initialChatId ? { chat_id: initialChatId } : {}),
                };
                const run = await apiClient.post({
                    url: '/admin/agent/runs',
                    body,
                    // Match the SWR cache key mode (options.currentMode)
                    // so create/read live in the same live-vs-sandbox
                    // bucket. Without this the request would fall back
                    // to the customer `modeStore` inside `api_client`.
                    mode: options.currentMode,
                });
                options.refreshRunLists();
                await options.onRunCreated?.(run.id);
                return run.id;
            } catch (error) {
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Could not create admin chat',
                );
                return null;
            } finally {
                pendingCreateRunPromiseRef.current = null;
                setIsCreatingRun(false);
            }
        })();
        pendingCreateRunPromiseRef.current = createRunPromise;
        return createRunPromise;
    }, [apiClient, draftModel, draftTier, initialPageContext, options]);

    const removeImageAttachment = useCallback((attachmentId: string) => {
        const removed = imageAttachmentsRef.current.find(
            (attachment) => attachment.id === attachmentId,
        );
        if (removed?.previewUrl) {
            URL.revokeObjectURL(removed.previewUrl);
        }
        const nextAttachments = imageAttachmentsRef.current.filter(
            (attachment) => attachment.id !== attachmentId,
        );
        imageAttachmentsRef.current = nextAttachments;
        setImageAttachments(nextAttachments);
    }, []);

    const uploadImageFiles = useCallback(
        (files: Array<File>) => {
            if (files.length === 0) {
                return;
            }
            void (async () => {
                const remainingSlots =
                    MAX_IMAGES_PER_TURN - imageAttachmentsRef.current.length;
                if (remainingSlots <= 0) {
                    setErrorMessage(
                        `Attach at most ${MAX_IMAGES_PER_TURN} images.`,
                    );
                    return;
                }
                const filesToUpload = files.slice(0, remainingSlots);
                if (filesToUpload.length < files.length) {
                    setErrorMessage(
                        `Attach at most ${MAX_IMAGES_PER_TURN} images.`,
                    );
                }

                let runIdForImages = options.selectedRunId;
                if (!runIdForImages) {
                    runIdForImages =
                        pendingDraftRunIdRef.current ?? (await createRun());
                    if (runIdForImages) {
                        pendingDraftRunIdRef.current = runIdForImages;
                    }
                }
                if (!runIdForImages) {
                    setErrorMessage('Could not create admin chat for upload.');
                    return;
                }
                const uploadRunId = runIdForImages;

                const nextAttachments: Array<AdminAgentImageAttachment> =
                    filesToUpload.map((file) => ({
                        id: crypto.randomUUID(),
                        filename: file.name || null,
                        mimeType: file.type || 'application/octet-stream',
                        sizeBytes: file.size,
                        previewUrl: URL.createObjectURL(file),
                        status: 'uploading',
                        imageRef: null,
                    }));
                imageAttachmentsRef.current = [
                    ...imageAttachmentsRef.current,
                    ...nextAttachments,
                ];
                setImageAttachments(imageAttachmentsRef.current);

                await Promise.all(
                    nextAttachments.map(async (attachment, index) => {
                        const file = filesToUpload[index];
                        if (!file) {
                            return;
                        }
                        try {
                            const imageRef = await uploadAdminAgentImage({
                                runId: uploadRunId,
                                file,
                                mode: options.currentMode,
                            });
                            setImageAttachments((current) =>
                                updateImageAttachmentsRef(
                                    current.map((currentAttachment) =>
                                        currentAttachment.id === attachment.id
                                            ? {
                                                  ...currentAttachment,
                                                  filename:
                                                      imageRef.filename ??
                                                      currentAttachment.filename,
                                                  mimeType: imageRef.mime_type,
                                                  sizeBytes:
                                                      imageRef.size_bytes,
                                                  status: 'ready',
                                                  imageRef,
                                              }
                                            : currentAttachment,
                                    ),
                                ),
                            );
                        } catch (error) {
                            setImageAttachments((current) =>
                                updateImageAttachmentsRef(
                                    current.map((currentAttachment) =>
                                        currentAttachment.id === attachment.id
                                            ? {
                                                  ...currentAttachment,
                                                  status: 'failed',
                                                  errorMessage:
                                                      error instanceof Error
                                                          ? error.message
                                                          : 'Upload failed',
                                              }
                                            : currentAttachment,
                                    ),
                                ),
                            );
                        }
                    }),
                );
            })();

            function updateImageAttachmentsRef(
                nextAttachments: Array<AdminAgentImageAttachment>,
            ): Array<AdminAgentImageAttachment> {
                imageAttachmentsRef.current = nextAttachments;
                return nextAttachments;
            }
        },
        [createRun, options.currentMode, options.selectedRunId],
    );

    const submitMessage = useCallback(
        async (
            content: string,
            deliveryMode?: AdminAgentMessageDeliveryMode,
            submitOptions?: AdminAgentSubmitMessageOptions,
        ): Promise<AdminAgentSubmitMessageOutcome> => {
            const trimmedContent = content.trim();
            const readyImageRefs = imageAttachments
                .map((attachment) => attachment.imageRef)
                .filter(
                    (imageRef): imageRef is AdminAgentUploadedImageRef =>
                        imageRef !== null,
                );
            if (
                (!trimmedContent && readyImageRefs.length === 0) ||
                isSubmittingRef.current
            ) {
                return;
            }

            isSubmittingRef.current = true;
            setIsSending(true);
            setErrorMessage(null);
            const clientOperationId = crypto.randomUUID();
            let hasOptimisticPendingEvent = false;
            try {
                if (!options.selectedOrganizationId) {
                    return false;
                }
                if (
                    imageAttachments.some(
                        (attachment) => attachment.status === 'uploading',
                    )
                ) {
                    setErrorMessage('Wait for images to finish uploading.');
                    return false;
                }
                if (
                    imageAttachments.some(
                        (attachment) => attachment.status === 'failed',
                    )
                ) {
                    setErrorMessage(
                        'Remove failed image uploads before sending.',
                    );
                    return false;
                }

                let runIdForMessage = options.selectedRunId;
                if (!runIdForMessage) {
                    runIdForMessage =
                        pendingDraftRunIdRef.current ?? (await createRun());
                    if (runIdForMessage) {
                        pendingDraftRunIdRef.current = runIdForMessage;
                    }
                }
                if (!runIdForMessage) {
                    return false;
                }

                hasOptimisticPendingEvent = true;
                stageOptimisticUserMessage(
                    createOptimisticUserMessage(
                        clientOperationId,
                        trimmedContent,
                        readyImageRefs,
                        deliveryMode,
                    ),
                    runIdForMessage,
                );

                const skillPreloadHints =
                    submitOptions?.skillPreloadHints ?? [];
                const body = {
                    content: trimmedContent,
                    client_operation_id: clientOperationId,
                    ...(deliveryMode ? { delivery_mode: deliveryMode } : {}),
                    ...(readyImageRefs.length > 0
                        ? { images: readyImageRefs }
                        : {}),
                    ...(skillPreloadHints.length > 0
                        ? { skill_preload_hints: [...skillPreloadHints] }
                        : {}),
                };
                await apiClient.post({
                    url: '/admin/agent/runs/{run_id}/messages',
                    pathParams: { run_id: runIdForMessage },
                    body,
                    mode: options.currentMode,
                });
                clearImageAttachments();
                refreshRunEventStream();
                options.refreshRunLists();
                if (options.selectedRunId) {
                    void options.mutateRun();
                } else {
                    options.onDraftRunCommitted?.();
                    if (options.navigateOnCreate) {
                        pendingDraftRunIdRef.current = null;
                        await navigate({
                            to: `/admin/agent/${runIdForMessage}`,
                        });
                    }
                }
                return true;
            } catch (error) {
                if (hasOptimisticPendingEvent) {
                    removeOptimisticPendingEvent(clientOperationId);
                }
                if (isTransparentRateLimitError(error)) {
                    return;
                }
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Could not send message',
                );
                return false;
            } finally {
                isSubmittingRef.current = false;
                setIsSending(false);
            }
        },
        [
            apiClient,
            clearImageAttachments,
            createRun,
            imageAttachments,
            navigate,
            options,
            refreshRunEventStream,
            removeOptimisticPendingEvent,
            setIsSending,
            stageOptimisticUserMessage,
        ],
    );

    const updateRunSetting = useCallback(
        async <TValue>({
            fallbackErrorMessage,
            isUpdating,
            patchRun,
            setDraftValue,
            setIsUpdating,
            value,
        }: UpdateRunSettingOptions<TValue>) => {
            if (!options.selectedRunId) {
                setDraftValue(value);
                return;
            }
            if (isUpdating) {
                return;
            }

            setIsUpdating(true);
            setErrorMessage(null);
            try {
                const updated = await patchRun(options.selectedRunId);
                void options.mutateRun((currentRun) =>
                    currentRun
                        ? { ...currentRun, ...updated.runPatch }
                        : currentRun,
                );
                setDraftValue(updated.draftValue);
                options.refreshRunLists();
            } catch (error) {
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : fallbackErrorMessage,
                );
            } finally {
                setIsUpdating(false);
            }
        },
        [options],
    );

    const updateSelectedTier = useCallback(
        (tier: AdminAgentTier) =>
            updateRunSetting({
                value: tier,
                isUpdating: isUpdatingTier,
                setIsUpdating: setIsUpdatingTier,
                setDraftValue: updateDraftTier,
                fallbackErrorMessage: 'Could not update thinking mode',
                patchRun: async (runId) => {
                    const updatedRun = await apiClient.patch({
                        url: '/admin/agent/runs/{run_id}/tier',
                        pathParams: { run_id: runId },
                        body: { tier },
                        mode: options.currentMode,
                    });
                    return { runPatch: updatedRun, draftValue: tier };
                },
            }),
        [
            apiClient,
            isUpdatingTier,
            options.currentMode,
            updateDraftTier,
            updateRunSetting,
        ],
    );

    const updateSelectedModel = useCallback(
        (model: string | null) =>
            updateRunSetting({
                value: model,
                isUpdating: isUpdatingModel,
                setIsUpdating: setIsUpdatingModel,
                setDraftValue: setDraftModel,
                fallbackErrorMessage: 'Could not update model',
                patchRun: async (runId) => {
                    const updatedModel = await apiClient.patch({
                        url: '/admin/agent/runs/{run_id}/model',
                        pathParams: { run_id: runId },
                        body: { active_model: model },
                        mode: options.currentMode,
                    });
                    return {
                        runPatch: updatedModel,
                        draftValue: updatedModel.active_model,
                    };
                },
            }),
        [apiClient, isUpdatingModel, options.currentMode, updateRunSetting],
    );

    return {
        clearErrorMessage,
        createRun,
        draftModel,
        draftTier,
        errorMessage,
        imageAttachments,
        imageUploadDisabled: isCreatingRun || isSending,
        isCreatingRun,
        isSending,
        isUpdatingModel,
        isUpdatingTier,
        resetDraftRun,
        submitMessage,
        uploadImageFiles,
        removeImageAttachment,
        updateSelectedModel,
        updateSelectedTier,
    };
}

async function uploadAdminAgentImage({
    runId,
    file,
    mode,
}: {
    readonly runId: string;
    readonly file: File;
    // Caller-supplied mode: admin surfaces pass their admin toggle
    // value so uploads land in the same live-vs-sandbox bucket as
    // the run itself. Reading `modeStore` inline here would silently
    // couple uploads to the customer-app mode.
    readonly mode: Mode;
}): Promise<AdminAgentUploadedImageRef> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(
        `${getRuntimeConfig().apiUrl}/admin/agent/runs/${runId}/images`,
        {
            method: 'POST',
            credentials: 'include',
            headers: { 'X-Mode': mode },
            body: formData,
        },
    );
    const body = (await response.json().catch(() => null)) as {
        readonly detail?: string;
        readonly image?: AdminAgentUploadedImageRef;
    } | null;
    if (!response.ok || !body?.image) {
        throw new Error(
            body?.detail ?? `Image upload failed with ${response.status}`,
        );
    }
    return body.image;
}

function createOptimisticUserMessage(
    clientOperationId: string,
    trimmedContent: string,
    images: Array<AdminAgentUploadedImageRef>,
    deliveryMode?: AdminAgentMessageDeliveryMode,
): PhoebeAgentRunPendingEventRead {
    const nowIsoTimestamp = new Date().toISOString();
    return {
        id: clientOperationId,
        type: 'user_message',
        payload: {
            text: trimmedContent,
            ...(deliveryMode ? { delivery_mode: deliveryMode } : {}),
            ...(images.length > 0 ? { images } : {}),
        },
        status: 'pending',
        interrupts: false,
        external_ref: `admin_internal_chat_send:${clientOperationId}`,
        drained_into_event_id: null,
        rejected_reason: null,
        enqueued_by_user_id: null,
        created_at: nowIsoTimestamp,
        drained_at: null,
        status_updated_at: nowIsoTimestamp,
        visible_at: nowIsoTimestamp,
    };
}
